import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, Pagination, Spinner, PageHeader } from '@shared/ui';
import { useDebounced } from '@shared/lib/use-debounced';
import { useBuyRequestsFeedQuery } from '@entities/rfq';
import { BuyRequestCard, FeedFilters } from '@features/rfq-feed';

export function BuyRequestsFeedPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const debouncedQ = useDebounced(q);

  const category = params.get('category') ?? undefined;
  const country = params.get('country') ?? undefined;
  const page = Number(params.get('page') ?? 0);

  const query = useMemo(
    () => ({
      category,
      country,
      q: debouncedQ || undefined,
      page,
      size: 20,
    }),
    [category, country, debouncedQ, page],
  );

  const { data, isLoading, isError, error, refetch } = useBuyRequestsFeedQuery(query);

  const updateParam = (next: Record<string, string | undefined>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === '') merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('buyRequests.title')} subtitle={t('buyRequests.subtitle')} />

      <FeedFilters
        q={q}
        category={category}
        country={country}
        onChange={(next) => {
          if ('q' in next) {
            setQ(next.q ?? '');
            updateParam({ q: next.q ?? undefined, page: '0' });
            return;
          }
          updateParam({ ...next, page: '0' });
        }}
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : isError ? (
        <p>
          {t('products.loadFail', { message: error?.message ?? 'unknown' })}{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            {t('common.retry')}
          </button>
        </p>
      ) : data && data.items.length === 0 ? (
        <EmptyState
          title={t('buyRequests.empty.title')}
          description={t('buyRequests.empty.description')}
        />
      ) : data ? (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((rfq) => (
              <BuyRequestCard key={rfq.id} rfq={rfq} />
            ))}
          </div>
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            onPageChange={(p) => updateParam({ page: String(p) })}
          />
        </>
      ) : null}
    </div>
  );
}
